/**
 * File: apps/qbench-cp/reactapp/src/Dashboard/Pages/Orders/OrderList/DisplaySettings.js
 *
 * Purpose: Renders the order list "view settings" menu for toggling row density (collapsing
 *     samples/tests).
 *
 * Scope: Order list header display-settings control.
 *
 * Tags: customer-portal, orders
 *
 * Overview: Exposes a gear icon button that opens an action menu with two switches — collapse by
 *     samples and collapse by orders — reflecting and updating appState.displaySettings.collapse
 *     via the app store dispatch. On mount it restores any previously saved collapse settings from
 *     localStorage under STORAGE_KEY. Also exports a small reusable DensitySetting row (title,
 *     description, switch) used to render each menu item.
 *
 * Exports: OrderListDisplaySettings component (default); DensitySetting component; STORAGE_KEY
 *     constant.
 */

import { faCog } from '@fortawesome/free-solid-svg-icons';
import { useAppStore } from 'App/store';
import { useEffect } from 'react';
import { Form } from 'react-bootstrap';
import ActionMenu, { MenuItem } from 'shared/components/ActionMenu';
import IconButton from 'shared/components/IconButton';
import useActionMenu from 'shared/hooks/misc/useActionMenu';
import { safeJsonParse } from 'shared/utils/functions';
import styled from 'styled-components';

export const STORAGE_KEY = 'displaySettings';

export const Wrapper = styled(MenuItem)`
  &:hover {
    background-color: transparent !important;
  }
`;

export function DensitySetting({
  title,
  description,
  checked,
  onChange,
}) {
  return (
    <Wrapper as="div" className="hstack justify-content-between">
      <div style={{ cursor: 'default' }}>
        <div className="action-title">{title}</div>
        <div className="action-description mt-1">{description}</div>
      </div>
      <Form.Switch checked={checked} onChange={onChange} />
    </Wrapper>
  );
}

function OrderListDisplaySettings() {
  const { appState, appStateDispatch } = useAppStore();
  const { collapse } = appState.displaySettings;
  const actions = [
    { title: 'Density', isGroupSeparator: true },
    {
      title: 'Collapse By Samples',
      as: () => (
        <DensitySetting
          checked={collapse.allSamples}
          title="Collapse By Samples"
          description="Hide all Test information"
          onChange={() => collapseAll({ isOrders: false })}
        />
      ),
    },
    {
      title: 'Collapse By Orders',
      as: () => (
        <DensitySetting
          checked={collapse.allOrders}
          title="Collapse By Orders"
          description="Hide all Sample and Test information"
          onChange={() => collapseAll({ isOrders: true })}
        />
      ),
    },
  ];
  const { menuProps } = useActionMenu(actions);

  useEffect(() => {
    const storedSettings = safeJsonParse(localStorage.getItem(STORAGE_KEY));
    dispatch(storedSettings?.collapse, {
      matchingSamplesOnly: storedSettings?.matchingSamplesOnly,
    });
  }, []);

  function dispatch(collapse = {}, extra = {}) {
    appStateDispatch({
      type: 'SET_DISPLAY_SETTINGS',
      payload: { collapse, ...extra },
    });
  }

  function collapseAll({ isOrders = true }) {
    dispatch({
      [isOrders ? 'allOrders' : 'allSamples']: !(isOrders
        ? collapse.allOrders
        : collapse.allSamples),
    });
  }

  return (
    <ActionMenu maxWidth={14} {...menuProps} autoClose="outside">
      <IconButton icon={faCog} color="#888" tooltip="View Settings" />
    </ActionMenu>
  );
}

export default OrderListDisplaySettings;
